import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/context/authStore';
import Layout from '@/components/Layout';
import AdminLayout from '@/components/AdminLayout';
import LabLayout from '@/components/LabLayout';
import ErrorBoundary from '@/components/ErrorBoundary';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ForceChangePassword from '@/pages/ForceChangePassword';
import Dashboard from '@/pages/Dashboard';
import Products from '@/pages/Products';
import Inventory from '@/pages/Inventory';
import Fraccionados from '@/pages/Fraccionados';
import Alerts from '@/pages/Alerts';
import Owners from '@/pages/Owners';
import OwnerDetail from '@/pages/OwnerDetail';
import Patients from '@/pages/Patients';
import Categories from '@/pages/Categories';
import Tenants from '@/pages/Tenants';
import Users from '@/pages/Users';
import Grooming from '@/pages/Grooming';
import MedicalAppointments from '@/pages/MedicalAppointments';
import ServiceOrders from '@/pages/ServiceOrders';
import Sales from '@/pages/Sales';
import Budgets from '@/pages/Budgets';
import CashRegister from '@/pages/CashRegister';
import Suppliers from '@/pages/Suppliers';
import PurchaseOrders from '@/pages/PurchaseOrders';
import VetProfile from '@/pages/VetProfile';
import Reports from '@/pages/Reports';
import Finances from '@/pages/Finances';
import Reminders from '@/pages/Reminders';
import BillingSettings from '@/pages/BillingSettings';
import MySubscription from '@/pages/MySubscription';
// Admin pages
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminTenants from '@/pages/admin/AdminTenants';
import AdminSubscriptions from '@/pages/admin/AdminSubscriptions';
import AdminExpenses from '@/pages/admin/AdminExpenses';
import AdminTenantUsers from '@/pages/admin/AdminTenantUsers';
import AdminReminders from '@/pages/admin/AdminReminders';
// Lab pages
import LabDashboard from '@/pages/lab/LabDashboard';
import LabClinics from '@/pages/lab/LabClinics';
import LabPatients from '@/pages/lab/LabPatients';
import LabProtocols from '@/pages/lab/LabProtocols';
import ProtocolDetail from '@/pages/lab/ProtocolDetail';
import ReferenceValues from '@/pages/lab/ReferenceValues';
import LabSpeciesPage from '@/pages/lab/LabSpeciesPage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si debe cambiar contraseña y no está en la página de cambio, redirigir
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <>{children}</>;
  }
  // Si debe cambiar contraseña, redirigir a esa página
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  // Redirigir según el rol
  if (user?.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }
  if (user?.role === 'laboratory') {
    return <Navigate to="/lab" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function LandingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) {
    return <>{children}</>;
  }
  // Usuario autenticado: redirigir al dashboard correspondiente
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  if (user?.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }
  if (user?.role === 'laboratory') {
    return <Navigate to="/lab" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function ChangePasswordRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si NO debe cambiar contraseña, redirigir al dashboard correspondiente
  if (!user?.must_change_password) {
    if (user?.role === 'superadmin') {
      return <Navigate to="/admin" replace />;
    }
    if (user?.role === 'laboratory') {
      return <Navigate to="/lab" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: { fontSize: '14px' },
        success: { iconTheme: { primary: '#16a34a', secondary: '#fff' } },
        error: { duration: 6000 },
      }}
    />
    <Routes>
      <Route
        path="/"
        element={
          <LandingRoute>
            <Landing />
          </LandingRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/change-password"
        element={
          <ChangePasswordRoute>
            <ForceChangePassword />
          </ChangePasswordRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="fraccionados" element={<Fraccionados />} />
        <Route path="categories" element={<Categories />} />
        <Route path="owners" element={<Owners />} />
        <Route path="owners/:id" element={<OwnerDetail />} />
        <Route path="patients" element={<Patients />} />
        <Route path="sales" element={<Sales />} />
        <Route path="budgets" element={<Budgets />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="users" element={<Users />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="grooming" element={<Grooming />} />
        <Route path="medical-appointments" element={<MedicalAppointments />} />
        <Route path="service-orders" element={<ServiceOrders />} />
        <Route path="cash-register" element={<CashRegister />} />
        <Route path="finances" element={<Finances />} />
        <Route path="vet-profile" element={<VetProfile />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reminders" element={<Reminders />} />
        <Route path="billing" element={<BillingSettings />} />
        <Route path="my-subscription" element={<MySubscription />} />
      </Route>
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="expenses" element={<AdminExpenses />} />
        <Route path="users" element={<AdminTenantUsers />} />
        <Route path="reminders" element={<AdminReminders />} />
      </Route>
      {/* Lab Routes */}
      <Route
        path="/lab"
        element={
          <PrivateRoute>
            <LabLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<LabDashboard />} />
        <Route path="clinics" element={<LabClinics />} />
        <Route path="patients" element={<LabPatients />} />
        <Route path="protocols" element={<LabProtocols />} />
        <Route path="protocols/:id" element={<ProtocolDetail />} />
        <Route path="reference-values" element={<ReferenceValues />} />
        <Route path="species" element={<LabSpeciesPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}
